import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  invalidateAllPatientQueries,
  useCompleteTaskMutation,
  useOwnProfileQuery,
  useReplyUnlockTaskRequestMutation,
  useTaskHistoryQuery,
  useTaskLockMutation,
  useTaskQuery,
  useTaskUnlockMutation,
} from '@common/cache';
import useUserStore from 'store/user.store.ts';
import { toastApiError } from '@common/errors/error-formatting.ts';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { matchPath, useLocation } from 'react-router-dom';
import { useTaskEventListener } from './useTaskEventListener.tsx';
import { t } from 'i18next';
import {
  PATIENT_TASK_LOCK_PERIOD,
  PATIENT_TASK_LOCK_PERIOD_VARIATION,
} from '@common/types/constants.ts';
import { isTaskRequirement } from '../common/util.ts';

interface IUseWorklistItemProps {
  id: string;
  handleClose: () => void;
  openedFromPatient?: boolean;
}

const historyTabIndex: string = '6';

export const useWorklistItem = ({
  id,
  handleClose,
  openedFromPatient,
}: IUseWorklistItemProps) => {
  const { user } = useUserStore();
  const profile = useOwnProfileQuery();
  const client = useQueryClient();
  const { pathname } = useLocation();
  const matchPatientPage = matchPath('/patient/*', pathname);

  const lockTaskMutation = useTaskLockMutation(id);
  const unlockTaskMutation = useTaskUnlockMutation(id);
  const {
    data: task,
    isLoading,
    isFetching,
    refetch,
  } = useTaskQuery({
    task_id: id,
  });
  // Covers every save triggered for this task, no matter which tab/component
  // started it and whether that component is still mounted.
  const isSaveMutationPending =
    useIsMutating({ mutationKey: ['saveTask', id] }) > 0;
  const replyUnlockTaskRequestMutation = useReplyUnlockTaskRequestMutation(
    task?.id || '',
  );

  const [tabIndex, setTabIndex] = useState('1');
  const [newTabIndex, setNewTabIndex] = useState('1');
  const [isFormClean, setIsFormClean] = useState(true);
  const [triggerTabChange, setTriggerTabChange] = useState(false);
  const [triggerCloseDrawer, setTriggerCloseDrawer] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [isCompleteOpen, setIsCompleteOpen] = useState(false);
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [isUnlockTaskModalOpen, setIsUnlockTaskModalOpen] = useState(false);
  const [requesterName, setRequesterName] = useState('');
  const [isUnlockInProgress, setIsUnlockInProgress] = useState(false);
  const [isImproveCareNotesModalOpen, setIsImproveCareNotesModalOpen] =
    useState(false);
  const [aiGeneratedCareNotes, setAiGeneratedCareNotes] = useState('');
  const [isInsufficientContext, setIsInsufficientContext] = useState(false);
  const originalNotesRef = useRef('');

  const { event, handleUnlockLockedTask, handleClearEvent } =
    useTaskEventListener(task?.id || '');

  const handleCloseUnlockModal = async () => {
    try {
      await replyUnlockTaskRequestMutation.mutate({ approve: false });

      handleClearEvent();
      setIsUnlockTaskModalOpen(false);
      setRequesterName('');
    } catch (error) {
      toastApiError(error);
    }
  };

  const handleApproveTaskUnlock = async () => {
    try {
      await replyUnlockTaskRequestMutation.mutate({ approve: true });

      handleClearEvent();
      setIsUnlockTaskModalOpen(false);
      setRequesterName('');
    } catch (error) {
      toastApiError(error);
    }
  };

  const handleUnlockTaskAction = () => {
    handleUnlockLockedTask();
    setIsUnlockInProgress(true);
  };

  useEffect(() => {
    if (event && event.type === 'task.unlock_requested' && !openedFromPatient) {
      setIsUnlockTaskModalOpen(true);
      setRequesterName(event?.payload?.requester?.full_name);
    }

    if (event && event.type === 'task.locked') {
      refetch();
      setIsUnlockInProgress(false);
      setIsUnlockTaskModalOpen(false);
    }

    if (event && event.type === 'task.unlock_reply') {
      refetch();
      if (event.payload.approved) {
        toast.success(t('general.yourUnlockRequestWasApproved'));
      } else {
        toast.error(t('general.yourUnlockRequestWasDenied'));
      }
      setIsUnlockInProgress(false);
    }
  }, [event]);

  const { mutateAsync: completeAsync, isPending: isTaskCompleting } =
    useCompleteTaskMutation(id, task?.patient?.id);

  // `isSavingTask` state also covers the debounced window before the request
  // leaves, `isSaveMutationPending` covers requests this drawer did not start.
  const isTaskSaving = isSavingTask || isSaveMutationPending;

  const isFormDisabled =
    !!(task?.locked_by && user && task.locked_by.id !== user?.id) ||
    !task?.has_write_permission;

  const lockedBy =
    task?.locked_by && task.locked_by.role && task.locked_by.id !== user?.id
      ? {
          name: `${task?.locked_by.first_name} ${task?.locked_by.last_name}`,
          role: task?.locked_by.role,
        }
      : {
          name: '',
          role: '',
        };

  const taskHistory = useTaskHistoryQuery({
    task_id: id,
    page: historyPage,
    limit: 100,
    enabled: tabIndex === historyTabIndex,
  });

  const handleOpenComplete = () => {
    setIsCompleteOpen(true);
  };
  const handleCloseComplete = () => {
    setIsCompleteOpen(false);
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setNewTabIndex(newValue);
    setTriggerTabChange(true);
  };

  // The confirmation modal stays open (in its loading state) for the whole
  // request, so the care notes modal only ever appears with a suggestion in it.
  const confirmComplete = async () => {
    try {
      originalNotesRef.current = task?.notes || pharmacyTaskNotesValue || '';
      const response = await completeAsync({});
      setIsCompleteOpen(false);
      if (response.data.suggested_note) {
        setAiGeneratedCareNotes(response.data.suggested_note);
        setIsInsufficientContext(!!response.data.insufficient_context);
        setIsImproveCareNotesModalOpen(true);
      } else {
        toast.success('Task completed');
        handleClose();
      }
    } catch (error) {
      setIsCompleteOpen(false);
      toastApiError(error);
    }
  };

  const closeDrawer = async () => {
    if (isTaskSaving) {
      return;
    }

    handleClose();

    if (task?.patient.id && matchPatientPage) {
      await invalidateAllPatientQueries(client, task?.patient.id);
    }

    if (!isFormDisabled && !matchPatientPage) {
      await unlockTaskMutation.mutateAsync();
    }
  };

  const lockTask = async () => {
    await lockTaskMutation.mutateAsync();
  };

  const handleTriggerCloseDrawer = () => {
    if (isLoading) {
      return;
    }

    if (!isFormClean) {
      setTriggerCloseDrawer(true);
    } else {
      closeDrawer();
    }
  };

  const usiAiSuggestionOnComplete = async (suggestion: string) => {
    try {
      await completeAsync({
        final: true,
        note: suggestion,
        note_decision:
          suggestion === aiGeneratedCareNotes ? 'accepted' : 'edited',
      });
      setAiGeneratedCareNotes('');
      setIsImproveCareNotesModalOpen(false);
      toast.success('Task completed');
      handleClose();
    } catch (error) {
      toastApiError(error);
    }
  };

  const keepOriginalOnComplete = async () => {
    try {
      await completeAsync({
        final: true,
        note: task?.notes,
        note_decision: 'declined',
      });
      setAiGeneratedCareNotes('');
      setIsImproveCareNotesModalOpen(false);
      toast.success('Task completed');
      handleClose();
    } catch (error) {
      toastApiError(error);
    }
  };

  const handleCloseImproveCareNotesModal = () => {
    setIsImproveCareNotesModalOpen(false);
    setAiGeneratedCareNotes('');
    setIsInsufficientContext(false);
  };

  useEffect(() => {
    if (
      task &&
      !isFormDisabled &&
      !isLoading &&
      profile.data?.activity_status === 'active'
    ) {
      lockTask();
    }

    const interval = setInterval(
      () => {
        if (
          task &&
          !isFormDisabled &&
          !isLoading &&
          profile.data?.activity_status === 'active'
        ) {
          lockTask();
        } else {
          client.invalidateQueries({ queryKey: ['task'] });
        }
      },
      Math.round(
        PATIENT_TASK_LOCK_PERIOD +
          Math.random() * PATIENT_TASK_LOCK_PERIOD_VARIATION,
      ),
    );

    return () => {
      clearInterval(interval);
    };
  }, [task, profile.data?.activity_status]);

  useEffect(() => {
    if (tabIndex === historyTabIndex) {
      taskHistory.refetch();
    }
  }, [tabIndex]);

  useEffect(() => {
    if (isFormClean && newTabIndex !== tabIndex) {
      setTabIndex(newTabIndex);
      setTriggerTabChange(false);
    }
  }, [isFormClean, newTabIndex]);

  const pharmacyTaskNotes = task?.requirements.find(
    req => 'field_name' in req && req.field_name === 'task_notes',
  );

  const pharmacyTaskNotesValue =
    pharmacyTaskNotes && isTaskRequirement(pharmacyTaskNotes)
      ? pharmacyTaskNotes.field_value
      : '';

  return {
    task,
    tabIndex,
    handleChangeTab,
    taskHistory: taskHistory.data,
    historyPage,
    setHistoryPage,
    handleOpenComplete,
    handleCloseComplete,
    isCompleteOpen,
    confirmComplete,
    closeDrawer,
    isFormDisabled,
    lockedBy,
    isSavingTask: isTaskSaving,
    setIsSavingTask,
    handleUnlockTaskAction,
    isUnlockTaskModalOpen,
    handleCloseUnlockModal,
    handleApproveTaskUnlock,
    requesterName,
    isUnlockInProgress,
    isUnlockDisabled: !task?.has_write_permission,
    isTaskCompleting,
    historyTabIndex,
    isFormClean,
    setIsFormClean,
    triggerTabChange,
    setTriggerTabChange,
    setNewTabIndex,
    handleTriggerCloseDrawer,
    triggerCloseDrawer,
    setTriggerCloseDrawer,
    isImproveCareNotesModalOpen,
    handleCloseImproveCareNotesModal,
    usiAiSuggestionOnComplete,
    keepOriginalOnComplete,
    aiGeneratedCareNotes,
    isInsufficientContext,
    originalCareNotes: originalNotesRef.current,
    isLoading,
    // Initial load plus every background refetch (e.g. the invalidation that
    // follows a save), so the task data on screen is never treated as final
    // while a fresher version is on the way.
    isTaskLoading: isLoading || isFetching,
  };
};
